import { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import type { ConsentEventRow } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { PageHead, Emphasis, MonoLabel, BackLink } from '@/components/editorial';
import { labelFor } from '@/i18n/labels';
import { formatPreset } from '@/i18n/intl';

// Hub child of Settings (design-grammar §4.2/§4.4): reachable only from the
// "Legal" row on SettingsPage / TRFerSettingsPage, so it takes the depth-2
// wayfinding form — a "← {parent}" link above PageHead and a
// "{parent} · {kind}" eyebrow. The parent differs by role: TRFers have their
// own /trfer/settings; every other role lands on /settings. That split is
// declared data now (`ROLE_PARENTS` in `lib/parentFor.ts`, #1456), not a
// local helper, so the back link is the shared primitive and the label is the
// word the sidebar uses rather than one spelled here.

// #2109 R3-B4: the consent event types (backend CONSENT_EVENT_TYPES) as keys.
// A type the map does not know yet renders capitalised, as it always did.
const CONSENT_EVENT_LABEL_KEY = {
  accepted: 'legal:settingsPage.event.accepted',
  declined: 'legal:settingsPage.event.declined',
  revoked: 'legal:settingsPage.event.revoked',
  reaccepted: 'legal:settingsPage.event.reaccepted',
} as const;

function capitalised(eventType: string): string {
  return eventType.length > 0 ? eventType[0].toUpperCase() + eventType.slice(1) : eventType;
}

/** `toLocaleString()`’s shape (“1/15/2026, 12:00:00 PM” in en), in the reader’s language. */
const OCCURRED_AT = {
  year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
} as const;

export function LegalSettingsPage() {
  const { user } = useAuth();
  const { t } = useTranslation(['legal', 'common']);
  const [events, setEvents] = useState<ConsentEventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.legal.myStatus()
      .then((r) => setEvents(r.status === 'success' && r.data ? r.data.events : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 max-w-2xl">
      <BackLink role={user?.role} label="always" className="-ml-2.5" />

      <PageHead density="compact"
        eyebrow={t('settingsPage.eyebrow')}
        icon={FileText}
        title={<Trans t={t} i18nKey="settingsPage.title" components={{ em: <Emphasis>{null}</Emphasis> }} />}
        lede={t('settingsPage.lede')}
      />

      {loading ? (
        <div className="flex items-center gap-2 text-body-sm text-stevens-ink-3 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common:states.loading')}
        </div>
      ) : events.length === 0 ? (
        <p className="text-body-sm text-stevens-ink-3">{t('settingsPage.empty')}</p>
      ) : (
        <Card className="divide-y divide-border/60 shadow-none">
          {events.map((e, i) => (
            <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                {/* document_title is resolved server-side from LegalDocument at the
                    exact version consented to; fall back to the raw document_id
                    only when it comes back null. The id is no longer repeated in
                    the caption (#2425): it read "· community_disclosure". Both
                    lines wrap rather than truncate (#2421). */}
                <p lang="en" className="text-body-sm font-medium text-foreground break-words">
                  {e.document_title ?? e.document_id}
                </p>
                <p className="text-caption text-stevens-ink-3 break-words">
                  {t('settingsPage.eventLine', {
                    event: labelFor(CONSENT_EVENT_LABEL_KEY, e.event_type as keyof typeof CONSENT_EVENT_LABEL_KEY, t, capitalised(e.event_type)),
                    version: e.document_version,
                  })}
                </p>
              </div>
              <MonoLabel className="shrink-0 font-normal">
                {formatPreset(e.occurred_at, OCCURRED_AT)}
              </MonoLabel>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
